/**
 * Bulk edit — the whole division as rows.
 *
 * Nothing here validates anything. Every save goes to the `bulk_save` action,
 * which judges the entire proposed board through the same module the drag on
 * the grid uses (_shared/placement-validation.ts), and applies all of it or
 * none of it. This screen's job is to collect the edits, show what came back
 * against the right rows, and refuse to let a director wander off with unsaved
 * work in the box.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertTriangle, ArrowDown, ArrowUp, Loader2, Lock, Plus, RotateCcw, Save, Table2, Trash2, Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction as rawInvokeEdgeFunction } from '@/lib/invokeEdgeFunction';

/**
 * These grids read `res.ok` / `res.data` straight off the envelope our edge
 * functions return. `invokeEdgeFunction` hands back `{ data, error }`, so the
 * envelope is one level down — reading `res.ok` there is always undefined and
 * every action fails silently as "no draft schedule". Unwrap once, here, and
 * throw on transport errors so the existing catch blocks report them.
 */
async function invokeEdgeFunction(name: string, options?: any): Promise<any> {
  const { data, error } = await rawInvokeEdgeFunction(name, options);
  if (error) throw new Error(error.message || 'Request failed');
  return data;
}
import { formatGameNumber } from '@/lib/gameNumber';
import { formatTime } from '@/lib/dates';
import { summarizeEdits, firstRows } from '@/lib/bulkChangeSummary';

const ALL = '__all__';
const NONE = '__none__';
/**
 * "Everything that isn't pool play" — bracket, consolation and crossover.
 * The Bracket Games screen uses this so its list matches the count in the
 * sidebar, which counts every non-pool game. A consolation game that appears
 * in the count but not in the list is worse than no count at all.
 */
const NON_POOL = '__nonpool__';

type Game = {
  id: string;
  game_number: number | null;
  round_number: number | null;
  game_type: string;
  division: string | null;
  pool_id: string | null;
  pool_name: string | null;
  bracket_label: string | null;
  scheduled_date: string | null;
  scheduled_start_time: string | null;
  scheduled_end_time: string | null;
  court_id: string | null;
  team_a_registration_id: string | null;
  team_b_registration_id: string | null;
  team_a_name: string;
  team_b_name: string;
  game_status: string;
  locked: boolean;
  lock_reason: string | null;
};

type Edit = Partial<Pick<Game, 'scheduled_date' | 'scheduled_start_time' | 'court_id' | 'team_a_registration_id' | 'team_b_registration_id'>> & { delete?: boolean };

type Issue = { game_id: string; other_game_id?: string; code: string; message: string };

interface Props {
  eventId: string;
  refreshToken?: number;
  /** Divisions-first navigation: lock the grid to one division. */
  division?: string;
  /** Lock the grid to pool games or bracket games. */
  gameType?: 'pool' | 'bracket';
  onChanged?: () => void;
  /** Extra buttons rendered next to "Add game". */
  extraActions?: React.ReactNode;
}

const SORTS = ['game_number', 'division', 'pool', 'date', 'time', 'court', 'team_a', 'team_b', 'status'] as const;
type SortKey = typeof SORTS[number];

export default function BulkEditGrid({ eventId, refreshToken, division: lockDivision, gameType: lockType, onChanged, extraActions }: Props) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [issues, setIssues] = useState<Issue[]>([]);
  const [warnings, setWarnings] = useState<Issue[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [impact, setImpact] = useState<any>(null);
  /** What the pending save would break, measured server-side on the pending board. */
  const [newProblems, setNewProblems] = useState<any>(null);
  const [showAllChanges, setShowAllChanges] = useState(false);

  // Add one game, delete one game, delete a division's whole pool round.
  // All three go through the same rulebook the drag and the bulk save use.
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [addIssues, setAddIssues] = useState<Issue[]>([]);
  const [addForm, setAddForm] = useState<any>({
    division: '', game_type: 'pool', pool_id: NONE,
    team_a_registration_id: NONE, team_b_registration_id: NONE,
    court_id: NONE, scheduled_date: '', scheduled_start_time: '',
  });
  const [delGame, setDelGame] = useState<Game | null>(null);
  const [delInfo, setDelInfo] = useState<any>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeInfo, setWipeInfo] = useState<any>(null);
  const [wipeBusy, setWipeBusy] = useState(false);



  const [fDivision, setFDivision] = useState(lockDivision ?? ALL);
  const [fPool, setFPool] = useState(ALL);
  const [fDate, setFDate] = useState(ALL);
  const [fCourt, setFCourt] = useState(ALL);
  const [fTeam, setFTeam] = useState(ALL);
  const lockedType = lockType === 'bracket' ? NON_POOL : lockType;
  const [fType, setFType] = useState<string>(lockedType ?? ALL);
  const [sortKey, setSortKey] = useState<SortKey>('game_number');
  const [sortAsc, setSortAsc] = useState(true);

  // Keep the locked filters in step with the division picked in the sidebar.
  useEffect(() => {
    if (lockDivision) setFDivision(lockDivision);
    if (lockedType) setFType(lockedType);
  }, [lockDivision, lockedType]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'bulk_edit_data', event_id: eventId },
      });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Could not load the games.');
      setData(res.data);
      setEdits({});
      setIssues([]);
      setWarnings([]);
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not load the games.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { void load(); }, [load, refreshToken]);

  const games: Game[] = data?.games ?? [];
  const courts: any[] = data?.courts ?? [];
  const teams: any[] = data?.teams ?? [];
  const allowedCourts: Record<string, string[]> = data?.allowed_courts ?? {};
  const allowedCourtsByPool: Record<string, string[]> = data?.allowed_courts_by_pool ?? {};
  const divisionPools: any[] = data?.pools ?? [];
  const dirty = Object.keys(edits).length > 0;

  // Unsaved work is real work: warn on a browser navigation away.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const courtName = useMemo(
    () => new Map(courts.map((c: any) => [c.id, c.name])), [courts],
  );
  const issuesByGame = useMemo(() => {
    const m = new Map<string, Issue[]>();
    for (const i of issues) {
      if (!m.has(i.game_id)) m.set(i.game_id, []);
      m.get(i.game_id)!.push(i);
    }
    return m;
  }, [issues]);
  const warningsByGame = useMemo(() => {
    const m = new Map<string, Issue[]>();
    for (const w of warnings) {
      if (!m.has(w.game_id)) m.set(w.game_id, []);
      m.get(w.game_id)!.push(w);
    }
    return m;
  }, [warnings]);

  const valueOf = (g: Game, field: keyof Edit) => {
    const e = edits[g.id];
    if (e && Object.prototype.hasOwnProperty.call(e, field)) return (e as any)[field];
    return (g as any)[field];
  };

  const setField = (g: Game, field: keyof Edit, value: any) => {
    setEdits((prev) => {
      const next = { ...prev };
      const row = { ...(next[g.id] ?? {}) } as any;
      if ((g as any)[field] === value || ((g as any)[field] ?? null) === (value ?? null)) {
        delete row[field];
      } else {
        row[field] = value;
      }
      if (Object.keys(row).length === 0) delete next[g.id];
      else next[g.id] = row;
      return next;
    });
  };

  // Deleting is one act with one confirmation, not a queued edit: the director
  // is told by name what else the game feeds before anything goes.
  const openDelete = async (g: Game) => {
    setDelGame(g);
    setDelInfo(null);
    setDelBusy(true);
    try {
      const res: any = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'delete_game', event_id: eventId, game_id: g.id },
      });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Could not check that game.');
      setDelInfo(res.data);
    } catch (e: any) {
      setDelGame(null);
      toast.error(e?.message ?? 'Could not check that game.');
    } finally {
      setDelBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!delGame) return;
    setDelBusy(true);
    try {
      const res: any = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: {
          action: 'delete_game', event_id: eventId, game_id: delGame.id,
          confirm: true, discard_score: true,
        },
      });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Delete failed.');
      toast.success(res.data?.message ?? 'Game deleted.');
      setDelGame(null);
      setDelInfo(null);
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.message ?? 'Delete failed.');
    } finally {
      setDelBusy(false);
    }
  };



  const revertRow = (id: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const divisions = useMemo(
    () => [...new Set(games.map((g) => g.division).filter(Boolean))].sort() as string[], [games],
  );
  const pools = useMemo(
    () => [...new Set(games.map((g) => g.pool_name).filter(Boolean))].sort() as string[], [games],
  );
  const dates = useMemo(
    () => [...new Set(games.map((g) => g.scheduled_date).filter(Boolean))].sort() as string[], [games],
  );
  const types = useMemo(
    () => [...new Set(games.map((g) => g.game_type))].sort(), [games],
  );

  const rows = useMemo(() => {
    let out = games.filter((g) => {
      if (fDivision !== ALL && g.division !== fDivision) return false;
      if (fPool !== ALL && g.pool_name !== fPool) return false;
      if (fDate !== ALL && g.scheduled_date !== fDate) return false;
      if (fCourt !== ALL && g.court_id !== fCourt) return false;
      if (fType === NON_POOL ? g.game_type === 'pool' : (fType !== ALL && g.game_type !== fType)) return false;
      if (fTeam !== ALL
        && g.team_a_registration_id !== fTeam
        && g.team_b_registration_id !== fTeam) return false;
      return true;
    });
    const key = (g: Game): string | number => {
      switch (sortKey) {
        case 'game_number': return g.game_number ?? 9999;
        case 'division': return g.division ?? 'zzz';
        case 'pool': return g.pool_name ?? g.bracket_label ?? 'zzz';
        case 'date': return g.scheduled_date ?? 'zzzz';
        case 'time': return g.scheduled_start_time ?? 'zzzz';
        case 'court': return courtName.get(g.court_id ?? '') ?? 'zzzz';
        case 'team_a': return g.team_a_name;
        case 'team_b': return g.team_b_name;
        case 'status': return g.game_status;
      }
    };
    out = [...out].sort((a, b) => {
      const ka = key(a); const kb = key(b);
      const cmp = typeof ka === 'number' && typeof kb === 'number'
        ? ka - kb
        : String(ka).localeCompare(String(kb), undefined, { numeric: true });
      return sortAsc ? cmp : -cmp;
    });
    return out;
  }, [games, fDivision, fPool, fDate, fCourt, fTeam, fType, sortKey, sortAsc, courtName]);

  const buildEdits = () =>
    Object.entries(edits).map(([game_id, e]) => ({ game_id, ...e }));

  // What the pending edits are, in words: grouped by kind, before → after.
  const teamNameById = useMemo(
    () => new Map(teams.map((t: any) => [t.id, t.name])), [teams],
  );
  const changeGroups = useMemo(
    () => summarizeEdits(games as any[], edits as any, {
      courtName: (id) => (id ? (courtName.get(id) ?? 'a court') : ''),
      teamName: (id) => (id ? (teamNameById.get(id) ?? 'TBD') : 'TBD'),
    }),
    [games, edits, courtName, teamNameById],
  );
  const changeCount = useMemo(
    () => changeGroups.reduce((n, g) => n + g.rows.length, 0), [changeGroups],
  );
  const visibleChanges = useMemo(
    () => (showAllChanges ? { shown: changeGroups, hidden: 0 } : firstRows(changeGroups, 12)),
    [changeGroups, showAllChanges],
  );

  const call = (extra: Record<string, unknown>) =>
    invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'bulk_save', event_id: eventId, edits: buildEdits(), ...extra },
    }) as Promise<any>;

  const startSave = async () => {
    setSaving(true);
    setIssues([]);
    setWarnings([]);
    try {
      const res: any = await call({ preview: true });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Could not check these changes.');
      setWarnings(res.data?.warnings ?? []);
      if ((res.data?.issues ?? []).length) {
        setIssues(res.data.issues);
        toast.error(
          `Nothing saved — ${res.data.issues.length} problem${res.data.issues.length === 1 ? '' : 's'} to fix first.`,
        );
        return;
      }
      setImpact(res.data?.impact ?? null);
      setNewProblems(res.data?.new_problems ?? null);
      setShowAllChanges(false);
      setConfirmOpen(true);
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not check these changes.');
    } finally {
      setSaving(false);
    }
  };

  const commit = async () => {
    setSaving(true);
    try {
      const res: any = await call({ confirm: true });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Save failed.');
      if (!res.data?.applied) {
        setIssues(res.data?.issues ?? []);
        setConfirmOpen(false);
        toast.error('Nothing was saved — the schedule changed underneath these edits.');
        return;
      }
      toast.success(
        `${res.data.updated} game${res.data.updated === 1 ? '' : 's'} updated`
        + (res.data.deleted ? `, ${res.data.deleted} deleted` : '') + '.',
      );
      setConfirmOpen(false);
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Add one game ───────────────────────────────────────────────────────────
  const submitAdd = async () => {
    setAddBusy(true);
    setAddIssues([]);
    try {
      const res: any = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: {
          action: 'add_game',
          event_id: eventId,
          division: addForm.division,
          game_type: addForm.game_type,
          pool_id: addForm.pool_id === NONE ? null : addForm.pool_id,
          team_a_registration_id: addForm.team_a_registration_id === NONE ? null : addForm.team_a_registration_id,
          team_b_registration_id: addForm.team_b_registration_id === NONE ? null : addForm.team_b_registration_id,
          court_id: addForm.court_id === NONE ? null : addForm.court_id,
          scheduled_date: addForm.scheduled_date || null,
          scheduled_start_time: addForm.scheduled_start_time || null,
        },
      });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Could not add that game.');
      if (!res.data?.created) {
        setAddIssues(res.data?.issues ?? []);
        toast.error('Nothing was added — fix the problems below.');
        return;
      }
      toast.success(res.data.message ?? 'Game added.');
      setAddOpen(false);
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not add that game.');
    } finally {
      setAddBusy(false);
    }
  };

  // ── Delete every pool game in one division ────────────────────────────────
  const openWipe = async () => {
    setWipeInfo(null);
    setWipeOpen(true);
    setWipeBusy(true);
    try {
      const res: any = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'delete_all_pool_games', event_id: eventId, division: fDivision },
      });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Could not count those games.');
      setWipeInfo(res.data);
    } catch (e: any) {
      setWipeOpen(false);
      toast.error(e?.message ?? 'Could not count those games.');
    } finally {
      setWipeBusy(false);
    }
  };

  const confirmWipe = async () => {
    setWipeBusy(true);
    try {
      const res: any = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: {
          action: 'delete_all_pool_games', event_id: eventId, division: fDivision,
          confirm: true, discard_scores: true,
        },
      });
      if (!res?.ok) throw new Error(res?.error?.message ?? 'Delete failed.');
      toast.success(res.data?.message ?? 'Pool games deleted.');
      setWipeOpen(false);
      setWipeInfo(null);
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.message ?? 'Delete failed.');
    } finally {
      setWipeBusy(false);
    }
  };


  const SortHead = ({ k, children }: { k: SortKey; children: React.ReactNode }) => (
    <th className="py-2 px-2 text-left font-medium">
      <button
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => { if (sortKey === k) setSortAsc((v) => !v); else { setSortKey(k); setSortAsc(true); } }}
      >
        {children}
        {sortKey === k && (sortAsc ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </th>
  );

  if (loading) {
    return (
      <Card><CardContent className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading games…
      </CardContent></Card>
    );
  }
  if (!data?.has_draft) {
    return (
      <Card><CardContent className="py-8 text-sm text-muted-foreground">
        No draft schedule yet — generate one first.
      </CardContent></Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Table2 className="h-4 w-4 text-muted-foreground" /> Bulk edit
          {dirty && (
            <Badge variant="secondary" className="ml-1">
              {Object.keys(edits).length} unsaved change{Object.keys(edits).length === 1 ? '' : 's'}
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          {dirty && (
            <Button size="sm" variant="ghost" onClick={() => { setEdits({}); setIssues([]); setWarnings([]); }}>
              <Undo2 className="h-3.5 w-3.5 mr-1" /> Discard all
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={saving}
            onClick={() => {
              setAddIssues([]);
              setAddForm((f: any) => ({
                ...f,
                division: fDivision !== ALL ? fDivision : (divisions[0] ?? ''),
                game_type: fType === NON_POOL ? 'bracket' : fType !== ALL ? fType : 'pool',
                pool_id: NONE,
              }));
              setAddOpen(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add game
          </Button>
          {extraActions}
          {/* Only ever on a pool-games view. On the bracket screen this button
              would delete a set of games the screen is not even showing. */}
          {lockedType !== NON_POOL && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive"
              disabled={saving || fDivision === ALL}
              title={fDivision === ALL ? 'Pick a division first' : `Delete every ${fDivision} pool game`}
              onClick={() => void openWipe()}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete all pool games
            </Button>
          )}

          <Button size="sm" variant="outline" onClick={() => void load()} disabled={saving}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reload
          </Button>

          <Button size="sm" onClick={() => void startSave()} disabled={!dirty || saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save changes
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Division', v: fDivision, set: setFDivision, opts: divisions },
            { label: 'Pool', v: fPool, set: setFPool, opts: pools },
            { label: 'Date', v: fDate, set: setFDate, opts: dates },
            { label: 'Type', v: fType, set: setFType, opts: types },
          ].map((f) => (
            <Select key={f.label} value={f.v} onValueChange={f.set as any}>
              <SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue placeholder={f.label} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All {f.label.toLowerCase()}s</SelectItem>
                {f.label === 'Type' && (
                  <SelectItem value={NON_POOL}>bracket, consolation &amp; crossover</SelectItem>
                )}
                {(f.opts as string[]).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          ))}
          <Select value={fCourt} onValueChange={setFCourt}>
            <SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue placeholder="Court" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All courts</SelectItem>
              {courts.map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fTeam} onValueChange={setFTeam}>
            <SelectTrigger className="h-8 w-[190px] text-xs"><SelectValue placeholder="Team" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All teams</SelectItem>
              {teams.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {issues.length > 0 && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Nothing was saved. Fix these {issues.length} problem{issues.length === 1 ? '' : 's'} and save again.
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              The whole set is checked together, so two changes that only clash with each other are caught here
              rather than half-applied.
            </p>
          </div>
        )}

        <div className="w-full min-w-0 overflow-x-auto rounded border">
          <table className="w-full min-w-[1100px] text-xs">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <SortHead k="game_number">Game</SortHead>
                <SortHead k="division">Division</SortHead>
                <SortHead k="pool">Pool</SortHead>
                <SortHead k="date">Date</SortHead>
                <SortHead k="time">Time</SortHead>
                <SortHead k="court">Court</SortHead>
                <SortHead k="team_a">Team A</SortHead>
                <SortHead k="team_b">Team B</SortHead>
                <SortHead k="status">Status</SortHead>
                <th className="py-2 px-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((g) => {
                const e = edits[g.id];
                const rowIssues = issuesByGame.get(g.id) ?? [];
                const rowWarnings = warningsByGame.get(g.id) ?? [];
                const deleting = !!e?.delete;
                const changed = !!e;
                const divCourts = (g.pool_id && allowedCourtsByPool[g.pool_id]) || (g.division ? allowedCourts[g.division] ?? courts.map((c: any) => c.id) : courts.map((c: any) => c.id));
                const courtOptions = courts.filter((c: any) => divCourts.includes(c.id));
                // A game can sit on a court its pool may not use. That court is
                // not offered below, so the Select would show blank — surface it
                // as a red extra option so the director sees where it is.
                const currentCourtId = valueOf(g, 'court_id') ?? null;
                const notAllowedCourt = currentCourtId
                  && g.pool_id && allowedCourtsByPool[g.pool_id]
                  && !allowedCourtsByPool[g.pool_id].includes(currentCourtId)
                  ? { id: currentCourtId, name: courtName.get(currentCourtId) ?? 'Court' }
                  : null;

                if (g.locked) {
                  return (
                    <tr key={g.id} className="border-t bg-muted/30 text-muted-foreground">
                      <td className="py-1.5 px-2 whitespace-nowrap font-medium">{formatGameNumber(g) || '—'}</td>
                      <td className="py-1.5 px-2">{g.division}</td>
                      <td className="py-1.5 px-2">{g.pool_name ?? g.bracket_label ?? '—'}</td>
                      <td className="py-1.5 px-2">{g.scheduled_date ?? '—'}</td>
                      <td className="py-1.5 px-2">{g.scheduled_start_time ? formatTime(g.scheduled_start_time) : '—'}</td>
                      <td className="py-1.5 px-2">{courtName.get(g.court_id ?? '') ?? '—'}</td>
                      <td className="py-1.5 px-2">{g.team_a_name}</td>
                      <td className="py-1.5 px-2">{g.team_b_name}</td>
                      <td className="py-1.5 px-2">{g.game_status}</td>
                      <td className="py-1.5 px-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <Lock className="h-3.5 w-3.5" /> Locked
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">{g.lock_reason}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr
                    key={g.id}
                    data-testid="bulk-edit-row"
                    className={[
                      'border-t align-top',
                      rowIssues.length ? 'bg-destructive/5' : changed ? 'bg-amber-50/60' : '',
                      deleting ? 'opacity-60 line-through' : '',
                    ].join(' ')}
                  >
                    <td className="py-1.5 px-2 whitespace-nowrap font-medium">{formatGameNumber(g) || '—'}</td>
                    <td className="py-1.5 px-2">{g.division}</td>
                    <td className="py-1.5 px-2">{g.pool_name ?? g.bracket_label ?? '—'}</td>
                    <td className="py-1.5 px-2">
                      <Input
                        type="date"
                        className="h-7 w-[130px] text-xs"
                        value={valueOf(g, 'scheduled_date') ?? ''}
                        disabled={deleting}
                        onChange={(ev) => setField(g, 'scheduled_date', ev.target.value || null)}
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <Input
                        type="time"
                        className="h-7 w-[105px] text-xs"
                        value={valueOf(g, 'scheduled_start_time') ?? ''}
                        disabled={deleting}
                        onChange={(ev) => setField(g, 'scheduled_start_time', ev.target.value || null)}
                      />
                    </td>
                    <td className="py-1.5 px-2">
                      <Select
                        value={valueOf(g, 'court_id') ?? NONE}
                        disabled={deleting}
                        onValueChange={(v) => setField(g, 'court_id', v === NONE ? null : v)}
                      >
                        <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>No court</SelectItem>
                          {/* Courts this division may not use are not offered at all. */}
                          {courtOptions.map((c: any) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                          {/* The court the game is on right now, when its pool may not use it.
                              Selecting it again matches the game's court, so it is not a change. */}
                          {notAllowedCourt && (
                            <SelectItem
                              key={notAllowedCourt.id}
                              value={notAllowedCourt.id}
                              className="text-destructive"
                            >
                              {notAllowedCourt.name} (not allowed for this pool)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </td>
                    {(['team_a_registration_id', 'team_b_registration_id'] as const).map((field) => (
                      <td className="py-1.5 px-2" key={field}>
                        <Select
                          value={valueOf(g, field) ?? NONE}
                          disabled={deleting}
                          onValueChange={(v) => setField(g, field, v === NONE ? null : v)}
                        >
                          <SelectTrigger className="h-7 w-[170px] text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>TBD</SelectItem>
                            {teams
                              .filter((t: any) => !g.division || !t.division || t.division === g.division)
                              .map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    ))}
                    <td className="py-1.5 px-2 whitespace-nowrap">
                      {deleting ? <span className="text-destructive">will be deleted</span> : g.game_status}
                      {rowIssues.map((i) => (
                        <div key={i.code + i.message} className="mt-1 max-w-[260px] whitespace-normal text-destructive">
                          {i.message}
                        </div>
                      ))}
                      {rowWarnings.map((w) => (
                        <div key={w.code + w.message} className="mt-1 max-w-[260px] whitespace-normal text-amber-700">
                          {w.message}
                        </div>
                      ))}
                    </td>
                    <td className="py-1.5 px-2 whitespace-nowrap">
                      {changed && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Undo this row"
                          onClick={() => revertRow(g.id)}>
                          <Undo2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-destructive"
                        title="Delete this game"
                        onClick={() => void openDelete(g)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={10} className="py-6 text-center text-muted-foreground">No games match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground">
          Deleting asks first and happens straight away; everything else happens on save. A deleted game leaves
          its number as a gap — renumber the division from the Pools panel if you want the gaps closed.
        </p>
      </CardContent>

      {/* ── Add a game ─────────────────────────────────────────────────── */}
      <Dialog open={addOpen} onOpenChange={(v) => { if (!addBusy) setAddOpen(v); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Add a game</DialogTitle>
            <DialogDescription>
              Leave the court, date and time empty to park the game unplaced — it waits until you give it a slot.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="space-y-1">
              <Label className="text-xs">Division</Label>
              <Select value={addForm.division} onValueChange={(v) => setAddForm((f: any) => ({ ...f, division: v, pool_id: NONE }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick a division" /></SelectTrigger>
                <SelectContent>
                  {divisions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={addForm.game_type} onValueChange={(v) => setAddForm((f: any) => ({ ...f, game_type: v, pool_id: NONE }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['pool', 'bracket', 'consolation', 'crossover'].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {addForm.game_type === 'pool' && (
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Pool</Label>
                <Select value={addForm.pool_id} onValueChange={(v) => setAddForm((f: any) => ({ ...f, pool_id: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Pick a pool" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE} disabled>Pick a pool</SelectItem>
                    {divisionPools
                      .filter((p: any) => p.division === addForm.division)
                      .map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {addForm.division && !divisionPools.some((p: any) => p.division === addForm.division) && (
                  <p className="text-xs text-destructive">{addForm.division} has no pools yet. Create a pool first.</p>
                )}
              </div>
            )}

            {(['team_a_registration_id', 'team_b_registration_id'] as const).map((field, i) => (
              <div className="space-y-1" key={field}>
                <Label className="text-xs">{i === 0 ? 'Team A' : 'Team B'}</Label>
                <Select
                  value={addForm[field]}
                  onValueChange={(v) => setAddForm((f: any) => ({ ...f, [field]: v }))}
                >
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>TBD</SelectItem>
                    {teams
                      .filter((t: any) => !addForm.division || !t.division || t.division === addForm.division)
                      .map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}

            <div className="space-y-1">
              <Label className="text-xs">Court</Label>
              <Select value={addForm.court_id} onValueChange={(v) => setAddForm((f: any) => ({ ...f, court_id: v }))}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No court (unplaced)</SelectItem>
                  {courts
                    .filter((c: any) => !addForm.division
                      || (allowedCourts[addForm.division] ?? courts.map((x: any) => x.id)).includes(c.id))
                    .map((c: any) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Date</Label>
                <Input type="date" className="h-8 text-xs" value={addForm.scheduled_date}
                  onChange={(e) => setAddForm((f: any) => ({ ...f, scheduled_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Start</Label>
                <Input type="time" className="h-8 text-xs" value={addForm.scheduled_start_time}
                  onChange={(e) => setAddForm((f: any) => ({ ...f, scheduled_start_time: e.target.value }))} />
              </div>
            </div>
          </div>

          {addIssues.length > 0 && (
            <div className="rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {addIssues.map((i) => <div key={i.code + i.message}>{i.message}</div>)}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" disabled={addBusy} onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button disabled={addBusy || !addForm.division || (addForm.game_type === 'pool' && addForm.pool_id === NONE)} onClick={() => void submitAdd()}>
              {addBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add game
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete one game ────────────────────────────────────────────── */}
      <AlertDialog open={!!delGame} onOpenChange={(v) => { if (!v && !delBusy) { setDelGame(null); setDelInfo(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this game?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {!delInfo ? (
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Checking what this game affects…
                  </p>
                ) : (
                  <>
                    <p>{delInfo.message}</p>
                    {delInfo.game?.has_score && (
                      <p className="rounded border border-destructive/40 bg-destructive/5 p-2 text-destructive">
                        This game already has a score. Deleting it throws that result away.
                      </p>
                    )}
                    {(delInfo.dependents ?? []).length > 0 && (
                      <ul className="list-disc pl-5 text-amber-700">
                        {delInfo.dependents.map((d: any) => <li key={d.id}>{d.label}</li>)}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={delBusy}>Keep it</AlertDialogCancel>
            <AlertDialogAction
              disabled={delBusy || !delInfo}
              onClick={(ev) => { ev.preventDefault(); void confirmDelete(); }}
            >
              {delBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Delete game
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Delete every pool game in a division ───────────────────────── */}
      <AlertDialog open={wipeOpen} onOpenChange={(v) => { if (!wipeBusy) { setWipeOpen(v); if (!v) setWipeInfo(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete all {wipeInfo?.total ?? ''} pool games{fDivision !== ALL ? ` in ${fDivision}` : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {!wipeInfo ? (
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Counting…
                  </p>
                ) : (
                  <>
                    <p>
                      This removes the games and everything on them. Bracket games are not
                      affected. This cannot be undone from here — use History to restore an
                      earlier arrangement.
                    </p>
                    <p className="text-muted-foreground">{wipeInfo.message}</p>
                    {wipeInfo.with_scores > 0 && (
                      <p className="rounded border border-destructive/40 bg-destructive/5 p-2 text-destructive">
                        {wipeInfo.with_scores} of them have a score recorded. Those results are thrown away.
                      </p>
                    )}
                    {(wipeInfo.fed_games ?? []).length > 0 && (
                      <ul className="list-disc pl-5 text-amber-700">
                        {wipeInfo.fed_games.map((b: any) => <li key={b.id}>{b.label}</li>)}
                      </ul>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={wipeBusy}>Keep them</AlertDialogCancel>
            <AlertDialogAction
              disabled={wipeBusy || !wipeInfo}
              onClick={(ev) => { ev.preventDefault(); void confirmWipe(); }}
            >
              {wipeBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Delete {wipeInfo?.total ?? 0} game{wipeInfo?.total === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/*
        Before a bulk write, say what it is about to do — grouped by the kind of
        change, before and after on every line, and the problems it would create
        read back from the same publish check the board uses.
      */}
      <AlertDialog open={confirmOpen} onOpenChange={(v) => { if (!saving) setConfirmOpen(v); }}>
        <AlertDialogContent className="max-w-2xl" data-testid="bulk-save-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Save {changeCount} change{changeCount === 1 ? '' : 's'} to this schedule?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="max-h-[55vh] space-y-3 overflow-auto text-sm">
                {visibleChanges.shown.map((grp) => (
                  <div key={grp.kind} data-testid={`change-group-${grp.kind}`}>
                    <div className="font-medium text-foreground">{grp.title}</div>
                    <ul className="mt-1 space-y-1">
                      {grp.rows.map((r) => (
                        <li key={`${grp.kind}-${r.id}`} className="text-[12px]">
                          <span className="font-medium text-foreground">{r.game}</span>
                          {' · '}{r.before} → {r.after}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                {visibleChanges.hidden > 0 && (
                  <button
                    type="button"
                    className="text-[12px] underline"
                    onClick={() => setShowAllChanges(true)}
                  >
                    and {visibleChanges.hidden} more
                  </button>
                )}

                {newProblems && (
                  newProblems.error_count + newProblems.warning_count === 0 ? (
                    <p className="rounded border border-green-300 bg-green-50 p-2 text-green-900">
                      This will not create any new problems.
                    </p>
                  ) : (
                    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
                      <div className="font-medium">
                        This will create {newProblems.error_count + newProblems.warning_count} new
                        problem{newProblems.error_count + newProblems.warning_count === 1 ? '' : 's'}:
                      </div>
                      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[12px]">
                        {[...newProblems.errors, ...newProblems.warnings]
                          .slice(0, 10)
                          .map((p: any, i: number) => <li key={i}>{p.message}</li>)}
                      </ul>
                    </div>
                  )
                )}

                {impact?.published && (
                  <p className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
                    This schedule is published. {impact.teams_affected} team
                    {impact.teams_affected === 1 ? '' : 's'} — {(impact.team_names ?? []).slice(0, 6).join(', ')}
                    {(impact.team_names ?? []).length > 6 ? ` and ${impact.team_names.length - 6} more` : ''} — are
                    looking at the version that is live right now. Republish afterwards so they see this one.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={saving} onClick={(ev) => { ev.preventDefault(); void commit(); }}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Save {changeCount} change{changeCount === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
